import type { ChakraProps } from "@chakra-ui/react"
import { Box, Button, Flex, Text } from "@chakra-ui/react"
import { CloseIcon, InfoIcon, WarningIcon } from "components/SvgIcons"
import React from "react"
import { colours } from "@shared/ui.design-system"
import { space } from "theme/themeConfig"
import { RichTextRendererLite } from "components/Contentful/RichTextRenderer/RichTextRendererLite"
import type { Document } from "@contentful/rich-text-types"
import type { AlertType, Notification } from "./types"

type AlertMetadata = {
  borderColor: string
  backgroundColor: string
  Icon: JSX.Element
}

const alertConfig: Record<AlertType, AlertMetadata> = {
  error: {
    borderColor: colours.error.medium,
    backgroundColor: colours.error.light,
    Icon: <WarningIcon boxSize="2rem" color={colours.error.medium} mx={1} />,
  },
  warning: {
    borderColor: colours.warning.dark,
    backgroundColor: colours.warning.medium,
    Icon: <InfoIcon boxSize="2rem" color={colours.warning.dark} mx={1} />,
  },
  info: {
    borderColor: colours.info.medium,
    backgroundColor: colours.info.light,
    Icon: <InfoIcon boxSize="2rem" color={colours.primary.dark} mx={1} />,
  },
}

interface Props extends Notification {
  onClose?: () => void
  rteDetails?: Document
}

export const Alert: React.FC<Props & ChakraProps> = ({
  level,
  message,
  detail,
  rteDetails,
  handlingStrategy = "closeable",
  onClose,
  onRetry,
  ...chakraProps
}) => {
  const { backgroundColor, borderColor, Icon } = alertConfig[level]

  const renderActionButton = () => {
    const isClosable = handlingStrategy === "closeable" && onClose
    const isRetryable = handlingStrategy === "retryable" && onRetry

    if (isClosable) {
      return (
        <Box cursor="pointer" data-testid="alertClose" onClick={onClose}>
          <CloseIcon
            color={colours.primary.dark}
            boxSize="1.5rem"
            data-testid="cross-icon"
            aria-label="Close"
          />
        </Box>
      )
    }

    if (isRetryable) {
      return (
        <Button variant="transparent" height="unset" onClick={onRetry}>
          <Text m={0} variant="book1" textDecoration="underline">
            RETRY
          </Text>
        </Button>
      )
    }

    return null
  }

  return (
    <Flex
      backgroundColor={backgroundColor}
      padding={space[-5]}
      paddingLeft={0}
      borderLeft={`4px solid ${borderColor}`}
      alignItems="center"
      {...chakraProps}
    >
      <Box>{Icon}</Box>
      <Box flex="1">
        {message && (
          <Text variant="acuminSemiBold" wordBreak="break-word" mt={0}>
            {message}
          </Text>
        )}
        {detail && (
          <Text
            variant="acuminRegular"
            wordBreak="break-word"
            letterSpacing="0.02"
            mt={message ? "-5" : 0}
          >
            {detail}
          </Text>
        )}
        {rteDetails && (
          <Box
            css={{
              "& > p": {
                marginTop: 0,
              },
            }}
          >
            <RichTextRendererLite content={rteDetails} />
          </Box>
        )}
      </Box>
      {renderActionButton()}
    </Flex>
  )
}
